import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Input, Select, Tag, Space, Typography,
  message, Row, Col, Card, DatePicker, Tooltip, Badge, Modal
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, FilterOutlined,
  EyeOutlined, AuditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { auditoriaService } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ACCION_COLORS = { crear: 'success', editar: 'processing', eliminar: 'error' };
const ACCION_LABELS = { crear: 'Crear', editar: 'Editar', eliminar: 'Eliminar' };
const MODULO_COLORS = { productos: 'blue', movimientos: 'purple', categorias: 'cyan', usuarios: 'orange' };

function Auditoria() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [filters, setFilters] = useState({ search: '', accion: '', modulo: '', fechas: null });

  const fetchAuditoria = useCallback(async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.search) params.search = filterParams.search;
      if (filterParams.accion) params.accion = filterParams.accion;
      if (filterParams.modulo) params.modulo = filterParams.modulo;
      if (filterParams.fechas && filterParams.fechas[0]) {
        params.fecha_inicio = filterParams.fechas[0].format('YYYY-MM-DD');
        params.fecha_fin = filterParams.fechas[1].format('YYYY-MM-DD');
      }
      const res = await auditoriaService.getAll(params);
      setRegistros(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar registros de auditoría');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditoria();
  }, [fetchAuditoria]);

  const applyFilters = () => fetchAuditoria(filters);
  const clearFilters = () => {
    const cleared = { search: '', accion: '', modulo: '', fechas: null };
    setFilters(cleared);
    fetchAuditoria(cleared);
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
      render: (val) => (
        <div>
          <div style={{ fontSize: 12 }}>{dayjs(val).format('DD/MM/YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(val).format('HH:mm:ss')}</Text>
        </div>
      )
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario_nombre',
      key: 'usuario_nombre',
      width: 150,
      render: (nombre) => <Text strong>{nombre}</Text>
    },
    {
      title: 'Acción',
      dataIndex: 'accion',
      key: 'accion',
      width: 100,
      render: (accion) => (
        <Badge status={ACCION_COLORS[accion] || 'default'} text={ACCION_LABELS[accion] || accion} />
      )
    },
    {
      title: 'Módulo',
      dataIndex: 'modulo',
      key: 'modulo',
      width: 120,
      render: (modulo) => (
        <Tag color={MODULO_COLORS[modulo] || 'default'} style={{ textTransform: 'capitalize' }}>
          {modulo}
        </Tag>
      )
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">—</Text>
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (ip) => <Text type="secondary" style={{ fontSize: 11 }}>{ip || '—'}</Text>
    },
    {
      title: '',
      key: 'detalle',
      width: 50,
      render: (_, record) => {
        const hasData = record.datos_anteriores || record.datos_nuevos;
        return hasData ? (
          <Tooltip title="Ver detalle">
            <Button
              size="small"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setDetailRecord(record)}
            />
          </Tooltip>
        ) : null;
      }
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <Space>
              <AuditOutlined />
              Registros de Auditoría
            </Space>
          </Title>
          <Text type="secondary">Historial de todas las acciones realizadas en el sistema</Text>
        </div>
      </div>

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={7}>
            <Input
              placeholder="Buscar en descripción o usuario..."
              prefix={<SearchOutlined />}
              allowClear
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Acción"
              allowClear
              style={{ width: '100%' }}
              value={filters.accion || undefined}
              onChange={(val) => setFilters((f) => ({ ...f, accion: val || '' }))}
            >
              <Option value="crear"><Badge status="success" text="Crear" /></Option>
              <Option value="editar"><Badge status="processing" text="Editar" /></Option>
              <Option value="eliminar"><Badge status="error" text="Eliminar" /></Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Módulo"
              allowClear
              style={{ width: '100%' }}
              value={filters.modulo || undefined}
              onChange={(val) => setFilters((f) => ({ ...f, modulo: val || '' }))}
            >
              <Option value="productos"><Tag color="blue">Productos</Tag></Option>
              <Option value="movimientos"><Tag color="purple">Movimientos</Tag></Option>
              <Option value="categorias"><Tag color="cyan">Categorías</Tag></Option>
              <Option value="usuarios"><Tag color="orange">Usuarios</Tag></Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha inicio', 'Fecha fin']}
              value={filters.fechas}
              onChange={(dates) => setFilters((f) => ({ ...f, fechas: dates }))}
            />
          </Col>
          <Col>
            <Button icon={<FilterOutlined />} type="primary" ghost onClick={applyFilters}>
              Filtrar
            </Button>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={clearFilters}>
              Limpiar
            </Button>
          </Col>
        </Row>
      </Card>

      <Card bordered={false}>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">{registros.length} registro(s) encontrado(s)</Text>
        </div>
        <Table
          dataSource={registros}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron registros de auditoría' }}
        />
      </Card>

      {/* Modal de detalle */}
      <Modal
        title="Detalle del Registro"
        open={!!detailRecord}
        onCancel={() => setDetailRecord(null)}
        footer={null}
        width={640}
      >
        {detailRecord && (
          <div>
            <p><Text strong>Fecha:</Text> {dayjs(detailRecord.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
            <p><Text strong>Usuario:</Text> {detailRecord.usuario_nombre}</p>
            <p><Text strong>Acción:</Text> <Badge status={ACCION_COLORS[detailRecord.accion]} text={ACCION_LABELS[detailRecord.accion]} /></p>
            <p><Text strong>Módulo:</Text> <Tag color={MODULO_COLORS[detailRecord.modulo]}>{detailRecord.modulo}</Tag></p>
            <p><Text strong>Descripción:</Text> {detailRecord.descripcion}</p>
            {detailRecord.datos_anteriores && (
              <>
                <Text strong>Datos anteriores:</Text>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, marginTop: 8, overflow: 'auto' }}>
                  {JSON.stringify(detailRecord.datos_anteriores, null, 2)}
                </pre>
              </>
            )}
            {detailRecord.datos_nuevos && (
              <>
                <Text strong>Datos nuevos:</Text>
                <pre style={{ background: '#f0f9eb', padding: 12, borderRadius: 6, fontSize: 12, marginTop: 8, overflow: 'auto' }}>
                  {JSON.stringify(detailRecord.datos_nuevos, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Auditoria;
